import { useId, type ComponentProps } from 'preact/compat'
import { InfoPopover } from './InfoPopover'

interface RadioButtonProps extends Omit<ComponentProps<'input'>, 'type'> {
  label?: string
  className?: string
  infoText?: string
  error?: boolean
  errorMessage?: string
}

export function RadioButton({
  label,
  className = '',
  infoText,
  error = false,
  errorMessage,
  id,
  checked,
  disabled,
  ...props
}: RadioButtonProps) {
  const radioId = id ?? useId()

  return (
    <div className={`flex items-start gap-2.5 ${className}`}>
      <div className='relative flex items-center'>
        <input
          type='radio'
          id={radioId}
          className='sr-only'
          checked={checked}
          disabled={disabled}
          {...props}
        />
        <label
          htmlFor={radioId}
          className={`relative flex items-center justify-center w-5 h-5 border-2 rounded-full cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-offset-2 bg-white ${
            error
              ? 'focus-within:ring-red-300'
              : 'focus-within:ring-primary-300'
          } ${
            checked
              ? error
                ? 'border-red-600'
                : 'border-cencert-navy-blue-light'
              : error
                ? 'border-red-500 hover:border-red-600'
                : 'border-gray-400 hover:border-gray-500'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div
            className={`w-3 h-3 rounded-full transition-opacity duration-200 ${
              checked ? 'opacity-100' : 'opacity-0'
            } ${error ? 'bg-red-600' : 'bg-cencert-navy-blue-light'}`}
          />
        </label>
      </div>
      {label && (
        <div className='flex items-start gap-2 flex-1'>
          <label
            htmlFor={radioId}
            className={`text-label cursor-pointer select-none ${
              error ? 'text-red-700' : 'text-gray-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {label}
          </label>
          {infoText && <InfoPopover infoText={infoText} placement='auto' />}
        </div>
      )}
      {error && errorMessage && (
        <div className='text-sm text-red-600 mt-1 ml-7'>{errorMessage}</div>
      )}
    </div>
  )
}
